import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {Box, FormHelperText, Grid, TextField} from '@mui/material'
import {PGButton, getProductLineColors} from '@progynyglobal/pg-ui-library'
import {Formik, FormikErrors, yupToFormErrors} from 'formik'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'
import * as Yup from 'yup'

import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {SetupProfileSubProps} from '@progyny/pages/SetUpProfile/types'
import {
  formatDateToFormattedString,
  isoFormat,
  isValidDate,
  StringDateParseError,
} from '@progyny/utils/dates'

const useStyles = makeStyles<{
  contractType?: string
  colors?: any
}>()((theme, {colors}) => ({
  dateField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: colors.primary,
      },
      '&:hover fieldset': {
        borderColor: colors.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
      },
    },
  },
}))

// Helper function to parse pregnancy date (due date or delivery date)
const parsePregnancyDate = (dateString: string | null | undefined) => {
  if (!!dateString) {
    const [year, month, day] = dateString
      .split('-', 3)
      .map((p) => parseInt(p, 10))
    return {
      year,
      month,
      day,
    }
  }

  return {
    day: '',
    month: '',
    year: '',
  }
}

const today = new Date()

const PregnancyDate: React.ComponentType<SetupProfileSubProps> = ({
  callback,
  persistMemberData,
  onboardingData,
  updateLoading,
  goBack,
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles({colors})
  const isDownSM = useResponsive('down', 'sm')

  const invalidDayError = t(
    'create_account_birth_date_day_is_required_and_must'
  )
  const invalidMonthError = t(
    'create_account_birth_date_month_is_required_and_must'
  )
  const invalidYearError = t('setup_profile_pregnancy_date_year_required')
  const invalidDueDateError = t('setup_profile_pregnancy_date_due_date_past')
  const invalidDeliveryDateError = t(
    'setup_profile_pregnancy_date_delivery_future'
  )
  const invalidDateError = t('setup_profile_pregnancy_date_invalid_date')

  // Determine if this is for due date or delivery date based on pregnancy status
  const isDueDate = onboardingData.pregnancyPostpartumStatus === 'pregnant'
  const isDeliveryDate =
    onboardingData.pregnancyPostpartumStatus === 'postpartum'

  // Get the appropriate initial date value based on pregnancy status
  const getInitialDateValue = () => {
    if (isDueDate && onboardingData.dueDate) {
      return parsePregnancyDate(onboardingData.dueDate)
    } else if (isDeliveryDate && onboardingData.deliveryDate) {
      return parsePregnancyDate(onboardingData.deliveryDate)
    }
    return {day: '', month: '', year: ''}
  }

  const initialValues = getInitialDateValue()

  const PregnancyDateSchema = Yup.object().shape({
    day: Yup.number()
      .required(invalidDayError)
      .integer(invalidDayError)
      .min(1, invalidDayError)
      .max(31, invalidDayError),
    month: Yup.number()
      .required(invalidMonthError)
      .integer(invalidMonthError)
      .min(1, invalidMonthError)
      .max(12, invalidMonthError),
    year: Yup.number()
      .required(invalidYearError)
      .integer(invalidYearError)
      .min(1900, invalidDateError)
      .max(today.getFullYear() + 1, invalidDateError) // Allow up to next year
      .test({
        name: 'validDate',
        exclusive: true,
        message: invalidDateError,
        test(value): boolean {
          return isValidDate({
            day: this.parent.day,
            month: this.parent.month,
            year: value,
          })
        },
      })
      .test({
        name: 'dueDateValidation',
        exclusive: true,
        message: invalidDueDateError,
        test(value): boolean {
          if (!isDueDate) return true
          const selectedDate = new Date(
            isoFormat({
              year: value,
              month: this.parent.month,
              day: this.parent.day,
            })
          )
          // Due date should not be older than today
          return selectedDate >= today
        },
      })
      .test({
        name: 'deliveryDateValidation',
        exclusive: true,
        message: invalidDeliveryDateError,
        test(value): boolean {
          if (!isDeliveryDate) return true
          const selectedDate = new Date(
            isoFormat({
              year: value,
              month: this.parent.month,
              day: this.parent.day,
            })
          )
          // Delivery date should not be greater than today
          return selectedDate <= today
        },
      }),
  })

  const dayElement = useRef<HTMLInputElement>()
  useEffect(() => {
    dayElement.current?.focus()
  }, [])

  function validateInitial(): FormikErrors<any> {
    try {
      PregnancyDateSchema.validateSync(initialValues)
      return {}
    } catch (err) {
      return yupToFormErrors(err)
    }
  }

  return (
    <Box sx={{pb: {xs: MOBILE_STICKY_HEIGHT, md: 0}}}>
      <Formik
        initialValues={initialValues}
        validationSchema={PregnancyDateSchema}
        onSubmit={(values) => {
          try {
            const {day, month, year} = values

            if (day && month && year) {
              const selectedDate = formatDateToFormattedString(
                new Date(year!, month! - 1, day!)
              )

              // Determine which field to update based on pregnancy status
              if (isDueDate) {
                persistMemberData({dueDate: selectedDate}, callback)
              } else if (isDeliveryDate) {
                persistMemberData({deliveryDate: selectedDate}, callback)
              } else {
                callback()
              }
            }
          } catch (e) {
            if (e instanceof StringDateParseError) {
              enqueueSnackbar({
                message: t(
                  'setup_profile_pregnancy_date_please_enter_valid_date'
                ),
                variant: 'error',
              })
            } else {
              throw e
            }
          }
        }}
        validateOnChange
        initialErrors={validateInitial()}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          isValid,
          handleBlur,
          handleChange,
          submitForm,
        }) => {
          return (
            <>
              <Box mt={4}>
                <Grid container spacing={2} justifyContent={'center'}>
                  <Grid xs={4} sm={3} item>
                    <TextField
                      size={'medium'}
                      variant='outlined'
                      margin={'none'}
                      label={t('common_day')}
                      id='day'
                      name='day'
                      value={values.day}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={isDownSM && touched.day && !!errors.day}
                      helperText={
                        isDownSM && touched.day ? <>{errors.day}</> : ''
                      }
                      inputRef={dayElement}
                      autoComplete={'bday-day'}
                      inputMode={'numeric'}
                      inputProps={{maxLength: '2'}}
                      className={classes.dateField}
                    />
                  </Grid>
                  <Grid xs={4} sm={3} item>
                    <TextField
                      size={'medium'}
                      variant='outlined'
                      margin={'none'}
                      label={t('common_month')}
                      id='month'
                      name='month'
                      value={values.month}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={isDownSM && touched.month && !!errors.month}
                      helperText={
                        isDownSM && touched.month ? <>{errors.month}</> : ''
                      }
                      autoComplete={'bday-month'}
                      inputMode={'numeric'}
                      inputProps={{maxLength: '2'}}
                      className={classes.dateField}
                    />
                  </Grid>
                  <Grid xs={4} sm={3} item>
                    <TextField
                      size={'medium'}
                      variant='outlined'
                      margin={'none'}
                      label={t('common_year')}
                      id='year'
                      name='year'
                      value={values.year}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={isDownSM && touched.year && !!errors.year}
                      helperText={
                        isDownSM && touched.year ? <>{errors.year}</> : ''
                      }
                      autoComplete={'bday-year'}
                      inputMode={'numeric'}
                      inputProps={{maxLength: '4'}}
                      className={classes.dateField}
                    />
                  </Grid>
                </Grid>
              </Box>
              {!isDownSM && dirty && !isValid && (
                <Box>
                  <FormHelperText error={!isValid}>
                    <>
                      {(touched.day && errors.day) ||
                        (touched.month && errors.month) ||
                        ((values.year || -1) > 999 && errors.year)}
                    </>
                  </FormHelperText>
                </Box>
              )}
              <MobileStickyActions desktopMt={6}>
                <Box display={'flex'} justifyContent={'space-between'} gap={2}>
                  <PGButton
                    label={t('common_back')}
                    disabled={updateLoading}
                    onClick={goBack}
                    variant='outlined'
                  />
                  <PGButton
                    data-role={isDueDate ? 'due-date' : 'delivery-date'}
                    label={t('common_continue')}
                    onClick={submitForm}
                    variant='contained'
                    disabled={!isValid || updateLoading}
                  />
                </Box>
              </MobileStickyActions>
            </>
          )
        }}
      </Formik>
    </Box>
  )
}

export default PregnancyDate
